import React, { useState, useEffect } from 'react';
import { Share2 } from 'lucide-react';
import { ClassData } from '../../types';
import { calculateEndTime, formatTime } from '../../helpers';
import { InfoModal } from '../common';

interface EditClassFormProps {
  classData: ClassData;
  onClose: () => void;
  onSave: (data: ClassData, updateFuture: boolean) => void;
  onShare: () => void;
}

const EditClassForm: React.FC<EditClassFormProps> = ({ classData, onClose, onSave, onShare }) => {
  // Safe Init: Ensure no fields are undefined
  const safeInit: ClassData = {
      ...classData,
      name: classData.name || 'Untitled Class',
      startTime: classData.startTime || '18:00',
      studio: classData.studio || 'Roots & Wings',
      paymentType: classData.paymentType || 'none',
      cost: classData.cost || null,
      allowArrangements: classData.allowArrangements || false,
      duration: classData.duration || 60,
      capacity: classData.capacity || 15,
      description: classData.description || '' 
  };
  
  // Force re-initialization when classData prop changes
  useEffect(() => {
    setEditForm(safeInit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classData.id]); 
  
  const [editForm, setEditForm] = useState<ClassData>(safeInit);
  const [applyToFuture, setApplyToFuture] = useState(false);
  const [validationError, setValidationError] = useState<{ isOpen: boolean; message: string }>({
    isOpen: false,
    message: ''
  });
  
  const editEndTime = calculateEndTime(editForm.startTime, typeof editForm.duration === 'string' ? 0 : editForm.duration);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
        const checked = (e.target as HTMLInputElement).checked;
        setEditForm({ ...editForm, [name]: checked });
    } else if (name === 'duration' || name === 'capacity' || name === 'cost') {
        setEditForm({ ...editForm, [name]: value === '' ? '' : Number(value) });
    } else {
        setEditForm({ ...editForm, [name]: value });
    }
  };
  
  const handleSave = () => {
      const finalDuration = typeof editForm.duration === 'string' ? 0 : editForm.duration;
      if (!editForm.name || !editForm.startTime || finalDuration <= 0) {
          setValidationError({
            isOpen: true,
            message: 'Please ensure Name, Start Time, and Duration are valid.'
          });
          return;
      }
      onSave({ ...editForm, endTime: editEndTime, duration: finalDuration }, applyToFuture);
  };

  return (
    <div className="space-y-4">
      {/* SHARE BUTTON IN EDIT */}
      <div className="flex justify-end">
         <button onClick={onShare} className="text-fuchsia-400 hover:text-fuchsia-300 text-sm flex items-center gap-1">
             <Share2 size={16} /> Share Class Link
         </button>
      </div>

      <div>
        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Class Name</label>
        <input name="name" className="w-full p-2 border border-slate-600 rounded bg-slate-800 text-slate-200" value={editForm.name} onChange={handleChange} />
      </div>
      
      <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Studio / Location</label>
          <input name="studio" className="w-full p-2 border border-slate-600 rounded bg-slate-800 text-slate-200" value={editForm.studio} onChange={handleChange} />
      </div>

      {/* DESCRIPTION FIELD */}
      <div>
        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Description (Optional)</label>
        <textarea
          name="description"
          className="w-full p-2 border border-slate-600 rounded bg-slate-800 text-slate-200"
          value={editForm.description || ''}
          onChange={handleChange}
          maxLength={2000}
          rows={3}
          placeholder="Add class description..."
        />
        <div className="text-right text-xs text-slate-500">{(editForm.description?.length || 0)}/2000</div>
      </div>

      {/* RESPONSIVE GRID: Stacks on mobile (1 col), 2 cols on tablet+ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
           <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Payment Type</label>
           <select name="paymentType" className="w-full p-2 border border-slate-600 rounded bg-slate-800 text-slate-200" value={editForm.paymentType} onChange={handleChange}>
             <option value="none">None</option> 
             <option value="venmo">Venmo</option>
           </select>
        </div>
        {editForm.paymentType === 'venmo' && (
           <div>
             <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Cost ($)</label>
             <input name="cost" type="number" placeholder="0 = Pay What You Can" className="w-full p-2 border border-slate-600 rounded bg-slate-800 text-slate-200" value={editForm.cost ?? ''} onChange={handleChange} />
           </div>
        )}
      </div>

      {editForm.paymentType === 'venmo' && (
         <div className="flex items-center gap-2">
            <input type="checkbox" id="editAllowArrangements" name="allowArrangements" checked={editForm.allowArrangements} onChange={handleChange} className="w-4 h-4" />
            <label htmlFor="editAllowArrangements" className="text-sm text-slate-400 cursor-pointer select-none">Allow "Alternate Arrangements"</label>
         </div>
      )}

      {/* RESPONSIVE GRID: Stacks on mobile (1 col), 3 cols on tablet+ */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase">Start</label>
          <input 
            name="startTime" 
            type="time" 
            className="w-full p-2 border border-slate-600 rounded bg-slate-800 text-slate-200 appearance-none" 
            style={{ WebkitAppearance: 'none' }}
            value={editForm.startTime} 
            onChange={handleChange} 
          />
        </div>
        <div><label className="block text-xs font-bold text-slate-500 uppercase">Dur (min)</label><input name="duration" type="number" className="w-full p-2 border border-slate-600 rounded bg-slate-800 text-slate-200" value={editForm.duration} onChange={handleChange} /></div>
        <div><label className="block text-xs font-bold text-slate-500 uppercase">End</label><input type="text" className="w-full p-2 border border-slate-600 rounded bg-slate-700 text-slate-400" readOnly value={formatTime(editEndTime)} /></div>
      </div>
      
      {/* RESPONSIVE GRID: Stacks on mobile (1 col), 2 cols on tablet+ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Capacity</label>
              <input name="capacity" type="number" placeholder="15" className="w-full p-2 border border-slate-600 rounded bg-slate-800 text-slate-200" value={editForm.capacity} onChange={handleChange} />
          </div>
          <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Instructor</label>
              <input name="instructor" className="w-full p-2 border border-slate-600 rounded bg-slate-800 text-slate-200" value={editForm.instructor} onChange={handleChange} />
          </div>
      </div>

      {classData.recurrenceId && (
        <div className="flex items-center gap-2 pt-2 border-t border-slate-700">
           <input type="checkbox" id="applyFuture" checked={applyToFuture} onChange={(e) => setApplyToFuture(e.target.checked)} className="w-4 h-4 text-rose-500" />
           <label htmlFor="applyFuture" className="text-sm font-bold text-slate-300">Apply to this and ALL future instances</label>
        </div>
      )}

      <div className="pt-4 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 bg-slate-600 rounded text-slate-100 hover:bg-slate-500">Cancel</button>
          <button onClick={handleSave} className="flex-1 py-2 bg-fuchsia-700 text-white rounded hover:bg-fuchsia-600">Save</button>
      </div>

      {/* Validation Error Modal */}
      <InfoModal
        isOpen={validationError.isOpen}
        onClose={() => setValidationError({ isOpen: false, message: '' })}
        title="Validation Error"
        message={validationError.message}
        variant="warning"
      />
    </div>
  );
};

export default EditClassForm;
